<template>
  <div class="h-full">
    <!-- Loading State -->
    <img
      v-if="state.isLoading"
      class="mx-auto"
      src="https://res.cloudinary.com/iubh/image/upload/v1613575530/07%20-%20Pages/Unit%20Bilder/Duales%20Studium/Bewerberportal/loading-bewerberportal-paper-p-unscreen_oa0wvh.gif"
      alt=""
    />

    <!-- Error State -->
    <div v-else-if="state.lastError" class="text-center py-8">
      <div class="text-lg text-red-600">{{ state.lastError }}</div>
      <button
        @click="loadFlow"
        class="mt-4 px-6 py-3 bg-green text-black border-2 border-black rounded-full font-bold uppercase hover:bg-green-light"
      >
        Neu laden
      </button>
    </div>

    <!-- Flow Content -->
    <div v-else class="h-full">
      <component
        v-if="currentComponent"
        :is="currentComponent"
        :current-step-id="currentStep?.id"
        :total-steps="flowSteps.length"
        :current-step-index="currentStepIndex"
        @request-next="onRequestNext"
        @request-back="(steps) => previousStep(steps)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type Component,
  computed,
  onMounted,
  provide,
  ref,
  shallowRef,
} from 'vue'
import stepResolver from '@/modules/wizard/services/step-resolver.service'
import wizardFlowService from '@/modules/wizard/services/wizard-flow.service'
import wizardStepContextService from '@/modules/wizard/services/wizard-step-context.service'
import { wizardFlows } from '@/modules/wizard/flows/wizard-flows'
import { isWizardFlowName } from '@/modules/wizard/functions/is-wizard-flow-name.function'
import { resolveSavedStepIndex } from '@/modules/wizard/functions/resolve-saved-step-index.function'
import { resolveStuckStep } from '@/modules/wizard/functions/resolve-stuck-step.function'
import { shouldRenderStep } from '@/modules/wizard/functions/should-render-step.function'
import type { StepLoader } from '@/modules/wizard/types/StepLoader'
import type { WizardFlowStepDefinition } from '@/modules/wizard/types/WizardFlowStepDefinition'
import type { WizardPreconditions } from '@/modules/wizard/types/WizardPreconditions'
import type { WizardStepContext } from '@/modules/wizard/types/WizardStepContext'
import { WizardFlowStepEvents } from '@/modules/wizard/types/WizardFlowStep'

const props = defineProps<{
  flowName: string
  context?: WizardPreconditions
}>()

const emit = defineEmits<WizardFlowStepEvents>()

const state = ref({
  isLoading: true,
  lastError: null as string | null,
})

const componentRegistry = shallowRef<Record<string, Component>>({})

const currentStepIndex = ref(0)
const flowSteps = ref<readonly WizardFlowStepDefinition[]>([])
const currentStep = computed(() => flowSteps.value[currentStepIndex.value])

const stepEvalContext = computed((): WizardStepContext | undefined =>
  props.context ? wizardStepContextService.build(props.context) : undefined,
)

/**
 * Returns the step's component loader if conditions pass and the id is registered;
 * otherwise `null`. Single `resolve` per check — use this for UI + recovery + next/prev.
 */
function tryResolveStepLoader(
  step: WizardFlowStepDefinition,
  ctx: WizardStepContext | undefined,
): StepLoader | null {
  if (!shouldRenderStep(step, ctx)) return null
  try {
    return stepResolver.resolve(step.id)
  } catch (err) {
    if (import.meta.env.DEV) {
      console.debug(`[WizardFlow] Step "${step.id}" not resolvable:`, err)
    }
    return null
  }
}

function isStepRenderableForUi(
  step: WizardFlowStepDefinition,
  ctx: WizardStepContext | undefined,
): boolean {
  return tryResolveStepLoader(step, ctx) !== null
}

/** Moves to `index` and saves it as the user's progress (by step id). */
const goToStep = (index: number) => {
  currentStepIndex.value = index
  const step = flowSteps.value[index]
  if (step) wizardFlowService.saveProgress(props.flowName, step.id)
}

// Get the next available step based on conditions
const getNextAvailableStep = (
  currentIndex: number,
  context: WizardStepContext | undefined,
): number => {
  for (let i = currentIndex + 1; i < flowSteps.value.length; i++) {
    if (isStepRenderableForUi(flowSteps.value[i], context)) {
      return i
    }
  }
  return -1 // No more available steps
}

// Get the previous available step based on conditions
const getPreviousAvailableStep = (
  currentIndex: number,
  context: WizardStepContext | undefined,
): number => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (isStepRenderableForUi(flowSteps.value[i], context)) {
      return i
    }
  }
  return -1 // No previous available steps
}

const currentComponent = computed(() => {
  if (!currentStep.value) return null

  const loader = tryResolveStepLoader(currentStep.value, stepEvalContext.value)
  if (!loader) return null

  const componentPath = loader.toString()
  return componentRegistry.value[componentPath] || null
})

// Called when a step requests auto-advance (after its internal logic)
const onRequestNext = () => nextStep()

const loadComponent = async (componentLoader: StepLoader) => {
  const componentPath = componentLoader.toString()
  if (componentRegistry.value[componentPath]) {
    return componentRegistry.value[componentPath]
  }

  try {
    const component = await componentLoader()
    componentRegistry.value[componentPath] = component.default
    return component.default
  } catch (err) {
    console.error(`Failed to load component: ${componentPath}`, err)
    return null
  }
}

const loadFlow = async () => {
  try {
    state.value.isLoading = true
    state.value.lastError = null

    // Settle everything derived condition keys depend on (e.g. experiment
    // variants) before the step list is evaluated.
    await wizardStepContextService.whenReady()

    if (!isWizardFlowName(props.flowName)) {
      throw new Error(`Unknown wizard flow: ${props.flowName}`)
    }

    const steps = wizardFlows[props.flowName].steps
    flowSteps.value = steps

    // Resume from the saved step. Save only when the stored value differs:
    // a fresh start, a removed step, or legacy index progress becomes a step id.
    const savedProgress = wizardFlowService.getProgress(props.flowName)
    const resumeIndex = resolveSavedStepIndex(steps, savedProgress)
    if (savedProgress === steps[resumeIndex]?.id) {
      currentStepIndex.value = resumeIndex
    } else {
      goToStep(resumeIndex)
    }

    // Preload all components for the flow
    for (const step of steps) {
      try {
        const componentLoader = stepResolver.resolve(step.id)
        await loadComponent(componentLoader)
      } catch (err) {
        console.error(`Failed to preload component for step ${step.id}:`, err)
      }
    }

    // Recover if saved progress points to a step that's no longer renderable
    reconcileSavedStepIfStuck()
  } catch (err) {
    console.error('Error loading flow:', err)
    state.value.lastError = `Failed to load flow: ${props.flowName}`
  } finally {
    state.value.isLoading = false
  }
}

/**
 * If the saved index points at a step that fails {@link isStepRenderableForUi}
 * (any `if`/`unless` in the flow definition, any flow), reconcile it via
 * {@link resolveStuckStep} — always forward or complete, never backward, so an
 * already-answered step is never re-shown. See {@link resolveStuckStep} for the
 * EEB placement (OCPT-860) motivation.
 */
const reconcileSavedStepIfStuck = () => {
  const steps = flowSteps.value
  if (!steps.length) return

  const ctx = stepEvalContext.value
  const saved = currentStepIndex.value

  const resolution = resolveStuckStep(steps.length, saved, (i) => {
    const step = steps[i]
    return step ? isStepRenderableForUi(step, ctx) : false
  })

  switch (resolution.type) {
    case 'stay':
      return
    case 'goto':
      goToStep(resolution.index)
      console.warn(
        `[WizardFlow] Saved step ${saved} not renderable; advanced to ${resolution.index}`,
      )
      return
    case 'complete':
      console.warn(
        `[WizardFlow] Saved step ${saved} not renderable and no later step applies; completing flow`,
      )
      emit('flow-complete')
      return
    case 'error':
      console.error('[WizardFlow] No renderable step for flow', props.flowName)
      state.value.lastError =
        'Dieser Schritt konnte nicht geladen werden. Bitte lade die Seite neu.'
      return
  }
}

const nextStep = () => {
  const nextIndex = getNextAvailableStep(
    currentStepIndex.value,
    stepEvalContext.value,
  )
  if (nextIndex !== -1) {
    goToStep(nextIndex)
  } else {
    // No more steps available, flow is complete
    emit('flow-complete')
  }
}

const previousStep = (steps: number = 1) => {
  let targetIndex = currentStepIndex.value
  let stepsRemaining = steps

  // Go back the specified number of steps
  while (stepsRemaining > 0 && targetIndex > 0) {
    const prevIndex = getPreviousAvailableStep(
      targetIndex,
      stepEvalContext.value,
    )
    if (prevIndex === -1) {
      break
    }
    targetIndex = prevIndex
    stepsRemaining--
  }

  if (targetIndex !== currentStepIndex.value) {
    goToStep(targetIndex)
  }
}

const renderableSteps = computed(() =>
  flowSteps.value.filter((step) =>
    isStepRenderableForUi(step, stepEvalContext.value),
  ),
)

const currentRenderableIndex = computed(() =>
  renderableSteps.value.findIndex((step) => step.id === currentStep.value?.id),
)

provide('wizardContext', {
  currentStepId: computed(() => currentStep.value?.id),
  totalSteps: computed(() => renderableSteps.value.length),
  currentStepIndex: computed(() => currentRenderableIndex.value),
})

onMounted(() => loadFlow())
</script>
